import * as React from "react";
import api, {
  SearchMansionResponse,
  SearchMansionRequest,
  FetchMansionTrafficResponse,
  FetchMansionUnitsResponse,
} from "@/apis/property";
import { useAsyncTaskLoading, useSnackBarCard } from "@/utils/hooks";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import contract, { Feature } from "@/utils/contract";
import { contractSearchMansion, ContractSearchMansionRequest } from "@/apis/contract";

export type InjectProps = {
  propertyName: string;
  mansions: SearchMansionResponse[];
  searchMansions: (name: string) => void;
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
  handleSelectMansion: (mansionId: string) => void;
  selectMansion: (
    mansion: SearchMansionResponse
  ) => (Promise<FetchMansionTrafficResponse[] | symbol> | Promise<FetchMansionUnitsResponse[] | symbol>)[];
};

const withEffect = (WrappedComponent: React.ElementType<Record<string, any> & InjectProps>) => {
  return (props: Record<string, any>) => {
    const {
      state: { contractId, featuresObj },
    } = React.useContext(ContractContext) || initContractStore(contract);
    const VALUATION_AREA = Boolean(featuresObj[Feature.VALUATION_AREA]);

    const [mansions, setMansions] = React.useState<SearchMansionResponse[]>([]);
    const { propertyName, selectMansion } = props;
    const [open, setOpen] = React.useState(false);
    const snackBarOpen = useSnackBarCard("alert");

    const { result: searchMansionsResult, start: searchMansions, error: searchMansionsError } = useAsyncTaskLoading<
      SearchMansionResponse[],
      [SearchMansionRequest]
    >(api.searchMansion);

    const {
      result: contractSearchMansionsResult,
      start: contractSearchMansions,
      error: contractSearchMansionsError,
    } = useAsyncTaskLoading<SearchMansionResponse[], [ContractSearchMansionRequest]>(contractSearchMansion);

    const searchMansionsHandler = React.useCallback(name => {
      if (VALUATION_AREA) {
        if (contractId != null) {
          contractSearchMansions({ contractId, name });
        } else {
          snackBarOpen("契約情報取得に失敗しました。恐れ入りますが再度お試しください。");
        }
      } else {
        searchMansions({ name });
      }
    }, []);

    const handleOpenModal = React.useCallback(() => {
      setOpen(() => true);
    }, []);

    const handleCloseModal = React.useCallback(() => {
      setOpen(() => false);
    }, []);

    const handleSelectMansion = React.useCallback(
      (mansionId: string) => {
        const mansion = mansions.find(searchedMansion => `${searchedMansion.id}` === mansionId);
        if (mansion) {
          // promise が resolve される場合のみダイアログを閉じる
          const promises = selectMansion(mansion) as [
            Promise<FetchMansionTrafficResponse[] | symbol>,
            Promise<FetchMansionUnitsResponse[] | symbol>
          ];
          Promise.all(promises).then(() => setOpen(() => false));
        }
      },
      [mansions]
    );

    React.useEffect(() => {
      if (searchMansionsResult) {
        setMansions(() => searchMansionsResult);
        setOpen(() => true);
      }
      if (searchMansionsError) {
        snackBarOpen("マンション情報の検索に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [searchMansionsResult, searchMansionsError]);

    React.useEffect(() => {
      if (contractSearchMansionsResult) {
        setMansions(() => contractSearchMansionsResult);
        setOpen(() => true);
      }
      if (contractSearchMansionsError) {
        snackBarOpen("マンション情報の検索に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [contractSearchMansionsResult, contractSearchMansionsError]);

    return (
      <WrappedComponent
        propertyName={propertyName}
        mansions={mansions}
        searchMansions={searchMansionsHandler}
        handleSelectMansion={handleSelectMansion}
        open={open}
        openModal={handleOpenModal}
        closeModal={handleCloseModal}
        selectMansion={selectMansion}
        {...props}
      />
    );
  };
};

export default withEffect;
